import * as React from 'react';
import { getAuctioneerLogoUrl, getAuctioneerLogoUrlPlaceholder, getImageUrl } from '@/utils/urls';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatShorterDate } from '@/utils/dates';
import { FormattedMessage } from 'react-intl';
import { getBidderReviewLocation } from '@/utils/address';
import IconPopover from '@/components/IconPopover/IconPopover';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import isEqual from 'react-fast-compare';
import RatingDisplaySmall from '@/components/RatingDisplaySmall/RatingDisplaySmall';
import ReviewFeedbackBar from './ReviewFeedbackBar';
import styles from './Review.module.scss';

type Props = {
    bidderCity: string;
    bidderCountry: string;
    bidderFirstName: string;
    bidderId: number;
    bidderState: string;
    catalogId: number;
    comments: any;
    helpfulCount: number;
    mobile: boolean;
    note: string;
    openReportReviewModal: Function;
    rating: number;
    review: string;
    reviewHeadline: string;
    reviewId: number;
    reviewSubmitted: string;
    seller: any;
    setBidderReviewStatus: Function;
    statuses: any;
    submitted: boolean;
    wonItems: any;
};

const getDisplayTime = (time: string) => {
    return formatShorterDate(new Date(time).getTime() / 1000);
};

const Review = (props: Props) => {
    const {
        bidderCity,
        bidderCountry,
        bidderFirstName,
        bidderState,
        catalogId,
        comments,
        helpfulCount,
        mobile,
        openReportReviewModal,
        rating,
        review,
        reviewHeadline,
        reviewId,
        reviewSubmitted,
        seller,
        setBidderReviewStatus,
        statuses,
        submitted,
        wonItems,
    } = props;

    const imageSize = !mobile ? 94 : 67;
    const imageCount = !mobile ? 3 : 1;

    const images = wonItems
        ? wonItems.slice(0, imageCount).map((item, index) => {
              return (
                  <ImageWithFallback
                      alt={item.title}
                      key={index}
                      src={getImageUrl({
                          catalogId: catalogId,
                          height: imageSize,
                          itemId: item.itemId,
                          sellerId: seller.sellerId,
                      })}
                  />
              );
          })
        : [];

    const imageView = <div className={styles.imageContainer}>{images}</div>;

    const bidder = (
        <div className={styles.reviewBidder}>
            {bidderFirstName}
            <IconPopover
                extraIcon
                extraIconClassName={styles.popoverExtraIcon}
                headline={<FormattedMessage id="review.verified_buyer_headline" />}
                icon={<FontAwesomeIcon icon={faCheckCircle} />}
                iconClassName={styles.popover}
                id="review-popover"
                text={<FormattedMessage id="review.verified_buyer_text" />}
            />
            <div className={styles.muted}>{getBidderReviewLocation(bidderCity, bidderState, bidderCountry)}</div>
        </div>
    );

    const ratingsAndDate = (
        <div className={styles.reviewHeader}>
            <div className={styles.bidderRatingContainer}>
                <RatingDisplaySmall hideReviewCount rating={rating} starSize="20px" />
            </div>
            <div className={styles.bidderDateContainer}>
                <span>{getDisplayTime(reviewSubmitted)}</span>
            </div>
        </div>
    );

    const publicComments = comments
        .filter((rc) => rc.privacyStatus === 'public')
        .map((rc) => {
            return (
                <React.Fragment key={rc.commentId}>
                    <div className={styles.commentResponse}>
                        {Boolean(seller.logoId) && (
                            <div className={styles.logoContainer}>
                                <ImageWithFallback
                                    alt={`${seller.name} logo`}
                                    className={styles.houseLogo}
                                    placeholder={getAuctioneerLogoUrlPlaceholder(seller.logoId, 84)}
                                    src={getAuctioneerLogoUrl(seller.logoId, 84)}
                                />
                            </div>
                        )}
                        <div className={styles.commentContainer}>
                            <div className={styles.commentResponseHeader}>
                                <span className={styles.commentDisplayName}>{rc.userDisplayName}</span>
                                <span className={styles.commentTime}>{getDisplayTime(rc.commentSubmitted)}</span>
                            </div>
                            <div className={styles.commentText}>{rc.comment}</div>
                        </div>
                    </div>
                </React.Fragment>
            );
        });

    const reviewMessaging = (
        <div className={styles.publicReview}>
            {reviewHeadline && <span className={styles.reviewHeadline}>{reviewHeadline}</span>}
            {review && <span className={styles.reviewText}>{review}</span>}
            {(review?.length > 0 || reviewHeadline?.length > 0) && (
                <ReviewFeedbackBar
                    helpfulCount={helpfulCount}
                    openReportReviewModal={openReportReviewModal}
                    reviewId={reviewId}
                    setBidderReviewStatus={setBidderReviewStatus}
                    statuses={statuses}
                    submitted={submitted}
                />
            )}
            {publicComments.length ? publicComments : null}
        </div>
    );

    const mobileView = (
        <div className={styles.mobileReviewContainer}>
            {imageView}
            <div className={styles.header}>
                {bidder}
                {ratingsAndDate}
            </div>
            <div className={styles.body}>{reviewMessaging}</div>
        </div>
    );

    return !mobile ? (
        <div className={styles.reviewContainer}>
            <div className={styles.leftSide}>{bidder}</div>
            <div className={styles.rightSide}>
                {ratingsAndDate}
                {reviewMessaging}
            </div>
            {imageView}
        </div>
    ) : (
        mobileView
    );
};

export default React.memo<Props>(Review, isEqual);
